// Small reusable form controls used by the board config editor.

interface Opt<T extends string> {
  value: T;
  label: string;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: Opt<T>[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="seg" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          data-active={o.value === value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  id,
}: {
  value: T;
  options: Opt<T>[];
  onChange: (v: T) => void;
  id?: string;
}) {
  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Ordered multi-select rendered as chips. Clicking toggles membership; selected
 * chips show their 1-based priority order. Used for tiebreakers (order matters)
 * and API-key scopes.
 */
export function ChipMulti<T extends string>({
  selected,
  options,
  onChange,
  ordered = false,
}: {
  selected: T[];
  options: Opt<T>[];
  onChange: (next: T[]) => void;
  ordered?: boolean;
}) {
  function toggle(v: T) {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  }
  return (
    <div className="chip-row">
      {options.map((o) => {
        const idx = selected.indexOf(o.value);
        const on = idx >= 0;
        return (
          <button key={o.value} type="button" className="chip" data-on={on} onClick={() => toggle(o.value)}>
            {ordered && on && <span className="ord">{idx + 1}</span>}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
